import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Alert, Button, CircularProgress, Card, CardContent
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { updatePaymentTrackingOnPageLoadAssessment as updateTrackingAPI } from '../../services/api';

const PaymentResponsePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState('');
    const [paymentDetails, setPaymentDetails] = useState(null);

    useEffect(() => {
        handleDirectResponse();
    }, []);

    const handleDirectResponse = async () => {
        const params = {};
        for (let [key, value] of searchParams.entries()) {
            params[key.replace(/\+/g, ' ')] = decodeURIComponent(value);
        }

        console.log("🔍 Payment Response Params:", params);

        const status = params.processedStatus?.toLowerCase() || 'failed';
        const message = decodeURIComponent(params.processedMessage || 'Payment response received.');

        const paymentInfo = {
            responseCode: params["Response Code"] || params.ResponseCode,
            subMerchantId: params.SubMerchantId,
            referenceNo: params.ReferenceNo,
            id: params.ID,
            rs: params.RS,
            processedStatus: params.processedStatus,
            processedMessage: message,
            orderId: params.orderId || 'Unknown',
            amount: params.amount || '0',
            tid: params.tid || '',
            tps: params.TPS,
            rsv: params.RSV,
        };

        setPaymentDetails(paymentInfo);
        setPaymentStatus(status);

        // Call the new API to update PaymentGateWayTracking table
        await updatePaymentTrackingOnPageLoad(paymentInfo);

        setLoading(false);
    };

    // New function to call the API that updates PaymentGateWayTracking table
    const updatePaymentTrackingOnPageLoad = async (paymentInfo) => {
        try {
            console.log("🔄 Calling API to update PaymentGateWayTracking on page load...");
            
            // Get appId and orgId from localStorage for spForm153Insert execution
            const appId = localStorage.getItem('appId') || paymentInfo.tid;
            const orgId = localStorage.getItem('orgId') || 
                         localStorage.getItem('userOrgId') || 
                         sessionStorage.getItem('orgId') || 
                         '1';
            
            console.log("📋 Retrieved from localStorage - AppId:", appId, "OrgId:", orgId);
            
            const apiPayload = {
                tid: paymentInfo.tid,
                referenceNo: paymentInfo.referenceNo,
                processedStatus: paymentInfo.processedStatus,
                responseCode: paymentInfo.responseCode,
                amount: paymentInfo.amount,
                paymentMode: 'Online',
                orderId: paymentInfo.orderId,
                processedMessage: paymentInfo.processedMessage,
                appId: appId, // Add appId for spForm153Insert
                orgId: orgId  // Add orgId for spForm153Insert
            };

            console.log("📤 API Payload:", apiPayload);

            const response = await updateTrackingAPI(apiPayload);
            
            if (response.data.success) {
                console.log("✅ PaymentGateWayTracking updated successfully:", response.data);
                
                // If payment was successful, show additional success message
                if (paymentInfo.processedStatus === 'success') {
                    console.log("🎉 Payment successful - Stage 18 update completed!");
                }
            } else {
                console.log("⚠️ PaymentGateWayTracking update response:", response.data);
            }

        } catch (error) {
            console.error("❌ Error updating PaymentGateWayTracking:", error);
            // Don't show error to user, just log it - this is a background operation
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0.00';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
    };

    const renderPaymentSuccess = () => (
        <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <CardContent>
                <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    Payment Successful!
                </Typography>
                <Alert severity="success" sx={{ mb: 3 }}>
                    {paymentDetails?.processedMessage || 'Your payment has been successfully completed.'}
                </Alert>

                <Box sx={{ textAlign: 'left', backgroundColor: 'grey.50', p: 2, borderRadius: 1 }}>
                    <Typography><strong>Reference No:</strong> {paymentDetails.referenceNo}</Typography>
                    <Typography><strong>Order ID:</strong> {paymentDetails.orderId}</Typography>
                    <Typography><strong>Amount:</strong> {formatCurrency(paymentDetails.amount)}</Typography>
                    <Typography><strong>Response Code:</strong> {paymentDetails.responseCode}</Typography>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button variant="contained" startIcon={<ReceiptIcon />} onClick={() => navigate('/main/dashboard')}>
                        Go to Dashboard
                    </Button>
                   
                </Box>
            </CardContent>
        </Card>
    );

    const renderPaymentFailed = () => (
        <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <CardContent>
                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    Payment Failed
                </Typography>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {paymentDetails?.processedMessage || 'Your payment could not be processed.'}
                </Alert>

                <Box sx={{ textAlign: 'left', backgroundColor: 'grey.50', p: 2, borderRadius: 1 }}>
                    <Typography><strong>Reference No:</strong> {paymentDetails.referenceNo}</Typography>
                    <Typography><strong>Order ID:</strong> {paymentDetails.orderId}</Typography>
                    <Typography><strong>Amount:</strong> {formatCurrency(paymentDetails.amount)}</Typography>
                    <Typography><strong>Response Code:</strong> {paymentDetails.responseCode}</Typography>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>                  
                    <Button variant="outlined" onClick={() => navigate('/main/dashboard')}>
                        Back to Application
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    const renderPaymentPending = () => (
        <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <CardContent>
                <WarningIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    Payment Pending
                </Typography>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {paymentDetails?.processedMessage || 'Your payment is being processed. Please check again later.'}
                </Alert>

                <Box sx={{ textAlign: 'left', backgroundColor: 'grey.50', p: 2, borderRadius: 1 }}>
                    <Typography><strong>Reference No:</strong> {paymentDetails.referenceNo}</Typography>
                    <Typography><strong>Order ID:</strong> {paymentDetails.orderId}</Typography>
                    <Typography><strong>Amount:</strong> {formatCurrency(paymentDetails.amount)}</Typography>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button variant="outlined" onClick={() => window.location.reload()}>
                        Refresh
                    </Button>
                    <Button variant="contained" onClick={() => navigate('/main/medical/declaration')}>
                        Continue to Application
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', flexDirection: 'column', alignItems: 'center', 
                justifyContent: 'center', minHeight: '100vh'
            }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Processing payment response...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 5, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
                    Payment Response
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    NABL Accreditation Payment Gateway Response
                </Typography>
            </Paper>

            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {paymentStatus === 'success' && renderPaymentSuccess()}
                {paymentStatus === 'failed' && renderPaymentFailed()}
                {paymentStatus === 'pending' && renderPaymentPending()}
            </Box>
        </Box>
    );
};

export default PaymentResponsePage;
